import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import './Profile.css';

export function Profile() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    sobrenome: user?.sobrenome || ''
  });

  // Atualizar formData quando o usuário for carregado
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        sobrenome: user.sobrenome || ''
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!user?.id) {
        throw new Error('Usuário não encontrado');
      }

      setIsLoading(true);
      setError('');
      setSuccess('');

      console.log('📝 Enviando atualização:', {
        userId: user.id,
        formData
      });

      // Usar o apiClient para fazer a requisição
      const response = await apiClient.fetch(`/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      console.log('📦 Dados do servidor:', response);
      
      if (response.user) {
        const updatedUser = { ...user, ...response.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        updateUser(updatedUser);
        setSuccess('Perfil atualizado com sucesso!');
        toast.success('Perfil atualizado com sucesso!');
        setIsEditing(false);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar perfil:', error);
      setError(error instanceof Error ? error.message : 'Erro ao atualizar perfil');
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      if (!user?.id) {
        throw new Error('Usuário não encontrado');
      }

      if (!password) {
        throw new Error('Por favor, informe sua senha para confirmar a exclusão');
      }

      setIsDeleting(true);
      setError('');

      // Usar o apiClient para fazer a requisição
      const response = await apiClient.fetch(`/users/${user.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ password })
      });

      if (response.success) {
        toast.success('Sua conta foi excluída com sucesso');
        logout();
        navigate('/');
      } else {
        throw new Error('Erro ao excluir conta');
      }
    } catch (error) {
      console.error('❌ Erro ao excluir conta:', error);
      setError(error instanceof Error ? error.message : 'Erro ao excluir conta');
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir conta');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setPassword('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCancel = () => {
    // Restaurar os valores originais
    setFormData({
      name: user?.name || '',
      sobrenome: user?.sobrenome || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleEditClick = () => {
    // Apenas habilitar o modo de edição
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="profile-container">
      <div className="profile-content">
        <h1>Meu Perfil</h1>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {!isEditing ? (
          // Modo de visualização
          <div className="profile-view">
            <div className="form-group">
              <label>Email</label>
              <div className="field-value">{user?.email || ''}</div>
              <small className="form-text">O email não pode ser alterado</small>
            </div>

            <div className="form-group">
              <label>Nome</label>
              <div className="field-value">{user?.name || ''}</div>
            </div>

            <div className="form-group">
              <label>Sobrenome</label>
              <div className="field-value">{user?.sobrenome || ''}</div>
            </div>

            <div className="button-group">
              <button
                type="button"
                onClick={handleEditClick}
                className="edit-button"
              >
                Editar Perfil
              </button>
              
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="delete-button"
              >
                Excluir Conta
              </button>
            </div>
          </div>
        ) : (
          // Modo de edição
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="input-disabled"
              />
              <small className="form-text">O email não pode ser alterado</small>
            </div>

            <div className="form-group">
              <label>Nome</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Seu nome"
              />
            </div>

            <div className="form-group">
              <label>Sobrenome</label>
              <input
                type="text"
                name="sobrenome"
                value={formData.sobrenome}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Seu sobrenome"
              />
            </div>

            <div className="button-group">
              <button 
                type="submit" 
                className="save-button"
                disabled={isLoading}
              >
                {isLoading ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-button"
                disabled={isLoading}
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" />
              </svg>
              Excluir Conta
            </h2>
            
            {error && <div className="error-message">{error}</div>}
            
            <p>Você está prestes a <span className="warning-text">excluir permanentemente</span> sua conta.</p>
            <p>Esta ação não pode ser desfeita e todos os seus dados serão removidos do sistema.</p>
            
            <p>Para confirmar a exclusão, digite sua senha:</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              if (password) {
                handleDeleteAccount();
              }
            }}>
              <div className="form-group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  disabled={isDeleting}
                />
              </div>
              
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPassword('');
                    setError('');
                  }}
                  className="cancel-button"
                  disabled={isDeleting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="delete-confirm-button"
                  disabled={isDeleting || !password}
                >
                  {isDeleting ? 'Excluindo...' : 'Confirmar Exclusão'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 